import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { AppShell } from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ForceChangePassword from "@/pages/auth/ForceChangePassword";

import Dashboard from "@/pages/Dashboard";
import MyWork from "@/pages/MyWork";
import Placeholder from "@/pages/Placeholder";
import Profile from "@/pages/Profile";
import UsersRoles from "@/pages/settings/UsersRoles";
import NewUser from "@/pages/settings/NewUser";
import AuditLog from "@/pages/settings/AuditLog";

import CrmDashboard from "@/pages/crm/CrmDashboard";
import LeadsBoard from "@/pages/crm/LeadsBoard";
import LeadDetail from "@/pages/crm/LeadDetail";
import Clients from "@/pages/crm/Clients";
import ClientDetail from "@/pages/crm/ClientDetail";
import QuotationBuilder from "@/pages/crm/QuotationBuilder";
import Pipelines from "@/pages/crm/Pipelines";
import ProjectTypes from "@/pages/crm/ProjectTypes";
import CrmReports from "@/pages/crm/CrmReports";

import Projects from "@/pages/pm/Projects";
import NewProject from "@/pages/pm/NewProject";
import ProjectLayout from "@/pages/pm/ProjectLayout";
import ProjectOverview from "@/pages/pm/ProjectOverview";
import Board from "@/pages/pm/Board";
import Backlog from "@/pages/pm/Backlog";
import WorkItemDetail from "@/pages/pm/WorkItemDetail";
import MeetingNotes from "@/pages/pm/MeetingNotes";
import TestCases from "@/pages/pm/TestCases";
import PmReports from "@/pages/pm/PmReports";
import PmPipelines from "@/pages/pm/PmPipelines";
import PmDashboard from "@/pages/pm/PmDashboard";
import Workload from "@/pages/pm/Workload";
import GlobalBoard from "@/pages/pm/GlobalBoard";
import EpicPlanning from "@/pages/pm/planning/EpicPlanning";
import ModulePlanning from "@/pages/pm/planning/ModulePlanning";
import TaskPlanning from "@/pages/pm/planning/TaskPlanning";
import TaskTemplates from "@/pages/pm/TaskTemplates";
import AllocationBoard from "@/pages/pm/AllocationBoard";
import ProjectDocuments from "@/pages/pm/ProjectDocuments";
import DependencyGraph from "@/pages/pm/DependencyGraph";

import Directory from "@/pages/hrms/Directory";
import NewEmployee from "@/pages/hrms/NewEmployee";
import EmployeeDetail from "@/pages/hrms/EmployeeDetail";
import DocumentTemplates from "@/pages/hrms/DocumentTemplates";
import Recruitment from "@/pages/hrms/Recruitment";
import HireCandidate from "@/pages/hrms/HireCandidate";
import GenerateDocument from "@/pages/hrms/GenerateDocument";
import Leave from "@/pages/hrms/Leave";
import ApplyLeave from "@/pages/hrms/ApplyLeave";
import SubmitReimbursement from "@/pages/hrms/SubmitReimbursement";
import Attendance from "@/pages/hrms/Attendance";
import Performance from "@/pages/hrms/Performance";
import ReviewForm from "@/pages/hrms/ReviewForm";
import ReviewDetail from "@/pages/hrms/ReviewDetail";
import PerformanceCycles from "@/pages/hrms/PerformanceCycles";
import EmployeeOfMonth from "@/pages/hrms/EmployeeOfMonth";
import Payroll from "@/pages/hrms/Payroll";
import PayrollConfig from "@/pages/hrms/PayrollConfig";
import Assets from "@/pages/hrms/Assets";
import Onboarding from "@/pages/hrms/Onboarding";
import Offboarding from "@/pages/hrms/Offboarding";
import MySpace from "@/pages/hrms/MySpace";
import ProfileRequests from "@/pages/hrms/ProfileRequests";
import HRDashboard from "@/pages/hrms/HRDashboard";

import DMS from "@/pages/dms/DMS";

import MyTimesheet from "@/pages/timesheets/MyTimesheet";
import AddEntry from "@/pages/timesheets/AddEntry";
import TimesheetApprovals from "@/pages/timesheets/Approvals";
import TimesheetStatus from "@/pages/timesheets/Status";

import Invoices from "@/pages/finance/Invoices";
import InvoiceDetail from "@/pages/finance/InvoiceDetail";
import FinanceDashboard from "@/pages/finance/FinanceDashboard";
import Expenses from "@/pages/finance/Expenses";
import CostCenters from "@/pages/finance/CostCenters";
import ChartOfAccounts from "@/pages/finance/ChartOfAccounts";

import Todo from "@/pages/todo/Todo";
import KnowledgeBase from "@/pages/kb/KnowledgeBase";
import CalendarPage from "@/pages/calendar/Calendar";
import Compliance from "@/pages/compliance/Compliance";
import Mailbox from "@/pages/mailbox/Mailbox";
import { Dashboards, StandardReports } from "@/pages/reporting/Reporting";
import { CompanySettings, CurrencySettings, BackupSettings } from "@/pages/settings/SystemSettings";
import SignatureDirectory from "@/pages/settings/SignatureDirectory";
import IntegrationsSettings from "@/pages/settings/IntegrationsSettings";
import UsageMetrics from "@/pages/settings/UsageMetrics";

function RequireAuth() {
  const { isAuthenticated, authChecked, authLoading, me } = useApp();
  const location = useLocation();

  if (!authChecked && authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (me?.must_change_password) {
    return <ForceChangePassword />;
  }
  return <AppShell />;
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route element={<RequireAuth />}>
            <Route index element={<Dashboard />} />
            <Route path="my-work" element={<MyWork />} />

            <Route path="crm/dashboard" element={<CrmDashboard />} />
            <Route path="crm/leads" element={<LeadsBoard />} />
            <Route path="crm/leads/:id" element={<LeadDetail />} />
            <Route path="crm/leads/:id/quotation/:qid" element={<QuotationBuilder />} />
            <Route path="crm/clients" element={<Clients />} />
            <Route path="crm/clients/:id" element={<ClientDetail />} />
            <Route path="crm/reports" element={<CrmReports />} />
            <Route path="crm/pipelines" element={<Pipelines />} />
            <Route path="crm/project-types" element={<ProjectTypes />} />

            <Route path="pm/projects" element={<Projects />} />
            <Route path="pm/projects/new" element={<NewProject />} />
            <Route path="pm/projects/:id" element={<ProjectLayout />}>
              <Route index element={<ProjectOverview />} />
              <Route path="planning/epics" element={<EpicPlanning />} />
              <Route path="planning/modules" element={<ModulePlanning />} />
              <Route path="planning/tasks" element={<TaskPlanning />} />
              <Route path="allocation" element={<AllocationBoard />} />
              <Route path="documents" element={<ProjectDocuments />} />
              <Route path="dependencies" element={<DependencyGraph />} />
              <Route path="board" element={<Board />} />
              <Route path="backlog" element={<Backlog />} />
              <Route path="test-cases" element={<TestCases />} />
              <Route path="meeting-notes" element={<MeetingNotes />} />
              <Route path="reports" element={<PmReports />} />
            </Route>
            <Route path="pm/projects/:id/items/:itemId" element={<WorkItemDetail />} />
            <Route path="pm/reports" element={<PmReports />} />
            <Route path="pm/pipelines" element={<PmPipelines />} />
            <Route path="pm/task-templates" element={<TaskTemplates />} />
            <Route path="pm/dashboard" element={<PmDashboard />} />
            <Route path="pm/workload" element={<Workload />} />
            <Route path="pm/global-board" element={<GlobalBoard />} />

            <Route path="settings/users" element={<UsersRoles />} />
            <Route path="settings/users/new" element={<NewUser />} />
            <Route path="settings/audit-log" element={<AuditLog />} />
            <Route path="settings/integrations" element={<IntegrationsSettings />} />
            <Route path="settings/usage-metrics" element={<UsageMetrics />} />
            <Route path="profile" element={<Profile />} />

            <Route path="hrms/analytics" element={<HRDashboard />} />
            <Route path="hrms/directory" element={<Directory />} />
            <Route path="hrms/directory/new" element={<NewEmployee />} />
            <Route path="hrms/directory/:id" element={<EmployeeDetail />} />
            <Route path="hrms/templates" element={<DocumentTemplates />} />
            <Route path="hrms/documents/generate" element={<GenerateDocument />} />
            <Route path="hrms/recruitment" element={<Recruitment />} />
            <Route path="hrms/recruitment/:candidateId/hire" element={<HireCandidate />} />
            <Route path="hrms/leave" element={<Leave />} />
            <Route path="hrms/leave/apply" element={<ApplyLeave />} />
            <Route path="hrms/leave/reimburse" element={<SubmitReimbursement />} />
            <Route path="hrms/attendance" element={<Attendance />} />
            <Route path="hrms/performance" element={<Performance />} />
            <Route path="hrms/performance/cycles" element={<PerformanceCycles />} />
            <Route path="hrms/performance/review/:cycleId/:employeeId/:reviewerType" element={<ReviewForm />} />
            <Route path="hrms/performance/:cycleId/:employeeId" element={<ReviewDetail />} />
            <Route path="hrms/recognition" element={<EmployeeOfMonth />} />
            <Route path="hrms/payroll" element={<Payroll />} />
            <Route path="hrms/payroll/config" element={<PayrollConfig />} />
            <Route path="hrms/assets" element={<Assets />} />
            <Route path="hrms/onboarding" element={<Onboarding />} />
            <Route path="hrms/offboarding" element={<Offboarding />} />
            <Route path="hrms/me" element={<MySpace />} />
            <Route path="hrms/profile-requests" element={<ProfileRequests />} />

            <Route path="dms" element={<DMS />} />

            <Route path="timesheets/week" element={<MyTimesheet />} />
            <Route path="timesheets/add-entry" element={<AddEntry />} />
            <Route path="timesheets/approvals" element={<TimesheetApprovals />} />
            <Route path="timesheets/status" element={<TimesheetStatus />} />

            <Route path="finance/invoices" element={<Invoices />} />
            <Route path="finance/invoices/:id" element={<InvoiceDetail />} />
            <Route path="finance/dashboard" element={<FinanceDashboard />} />
            <Route path="finance/expenses" element={<Expenses />} />
            <Route path="finance/cost-centers" element={<CostCenters />} />
            <Route path="finance/chart-of-accounts" element={<ChartOfAccounts />} />

            <Route path="reporting/dashboards" element={<Dashboards />} />
            <Route path="reporting/standard" element={<StandardReports />} />
            <Route path="todo" element={<Todo />} />
            <Route path="kb" element={<KnowledgeBase />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="mailbox" element={<Mailbox />} />

            <Route path="settings/company" element={<CompanySettings />} />
            <Route path="settings/signature-directory" element={<SignatureDirectory />} />
            <Route path="settings/currency" element={<CurrencySettings />} />
            <Route path="settings/backup" element={<BackupSettings />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="bottom-right" richColors />
    </AppProvider>
  );
}

export default App;
